// External Imports
import { Fragment, useState } from "react";
import { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal Imports
import Heading from "../UI/Heading";
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import DetailPair from "../UI/DetailPair";
import LoadingSpinner from "../UI/LoadingSpinner";
import { MdRefresh } from "react-icons/md";

/** ========================================================================
 * * AddAnnouncement Component
 * This component renders a form to create a new announcement.
 * ========================================================================== */

export default function AddAnnouncement() {
  const { navigate, setWarning } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [sendSMS, setSendSMS] = useState(true);
  const [result, setResult] = useState(null);

  /* ====================== FETCH RECORDS ====================== */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      setResult(null);
      const response = await requestAdminAPI("search", {
        table: params.get("table"),
        limit: params.get("limit" || 50),
        filters: Object.fromEntries(params.entries()),
      });

      response.forEach((rec) => {
        if (response[0].firstName)
          rec.name = `${rec.firstName} ${rec.lastName}`;
        rec.selected = true;
      });
      setRecords(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== ADD ANNOUNCEMENT ====================== */
  const confirmHandler = async () => {
    try {
      setLoading(true);
      if (!sendSMS && !sendEmail)
        throw new Error("Select at least one method to send the announcement.");
      const payload = {
        message: document.getElementById("message").value,
        description: document.getElementById("description").value,
        subject: document.getElementById("subject")?.value,
        isTest: false,
        recipientType: params.get("table"),
        recipients: [],
      };
      records.forEach((rec) => {
        if (!rec.selected) return;
        const entry = {
          name: rec.name,
          id: rec.id,
        };
        if (sendSMS) entry.phone = rec.phone;
        if (sendEmail) entry.email = rec.email;
        payload.recipients.push(entry);
      });
      const response = await requestAdminAPI("create-announcement", payload);
      setResult(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== TEST ANNOUNCEMENT ====================== */
  const testHandler = async (type) => {
    try {
      setLoading(true);
      setResult(null);
      let receipient = {
        name: "Test User",
        id: "test-id",
      };
      if (type === "sms")
        receipient.phone = document.getElementById("test-phone").value;
      if (type === "email")
        receipient.email = document.getElementById("test-email").value;
      const payload = {
        message: document.getElementById("message").value,
        description: document.getElementById("description").value,
        subject: document.getElementById("subject")?.value,
        isTest: true,
        recipientType: "Test",
        recipients: [receipient],
      };
      const response = await requestAdminAPI("create-announcement", payload);
      setResult(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Calculations
  const totalRecipients = records.filter((record) => record.selected).length;
  const estimatedTime =
    totalRecipients > 0 ? `${(totalRecipients / 60).toFixed(2)} minutes` : "-";

  useEffect(() => {
    if (params.get("table")) fetchRecords();
    else
      setWarning(
        "No recipients specified! To get a preselected list of recipients, go to the page with the list of recipients e.g. Partners and click the announcement icon on top right."
      );
  }, []);

  return (
    <div className="container">
      <Heading
        title="Add Announcement"
        text="To get preselected recipients go the page with the list of recipients e.g. Partners and click the announcement icon on top right."
      >
        <MdRefresh onClick={() => window.location.reload()} className="icon" />
      </Heading>

      <div className="info">
        <div className="lg:col-span-2">
          <p className="text-sm text-stone-400">Announcement:</p>
          <textarea
            className="w-full form-input"
            id="message"
            rows="5"
            placeholder="Type your announcement here..."
          />
        </div>
        <div className="lg:col-span-2">
          <p className="text-sm text-stone-400">Description:</p>
          <textarea
            className="w-full form-input"
            id="description"
            rows="5"
            placeholder="Describe why you are sending this announcement..."
          />
        </div>
      </div>

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : records.length > 0 ? (
        <Fragment>
          <div className="pl-6 info">
            {["Name", "Email", "Phone", "ID"].map((header) => (
              <p key={header} className="pl-2 font-semibold text-stone-200">
                {header}
              </p>
            ))}
          </div>
          <div className="list h-[400px] gap-2">
            {records.map((record, idx) => (
              <div className="flex items-center space-x-2" key={idx}>
                <input
                  type="checkbox"
                  className="checkbox"
                  defaultChecked={record.selected}
                  onChange={(e) => {
                    const updatedRecords = [...records];
                    updatedRecords[idx].selected = e.target.checked;
                    setRecords(updatedRecords);
                  }}
                />
                <div className="w-full info">
                  {["Name", "Email", "Phone", "ID"].map((field) => (
                    <input
                      className="form-input-disabled"
                      value={record[field.toLowerCase()]}
                      disabled
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <hr className="my-4 hr" />
        </Fragment>
      ) : (
        <NoRecordsFound />
      )}

      <div className="info lg:grid-cols-5">
        <div className="grid grid-cols-2 text-sm text-stone-200">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="checkbox"
              id="sms"
              onChange={(e) => setSendSMS(e.target.checked)}
              defaultChecked
            />
            <p>Send as SMS</p>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="checkbox"
              id="email"
              onChange={(e) => setSendEmail(e.target.checked)}
            />
            <p>Send as Email</p>
          </div>
        </div>
        <div className="w-full lg:col-span-2">
          {sendEmail && (
            <input
              className="w-full form-input"
              placeholder="Type email subject here..."
              id="subject"
            />
          )}
        </div>
        <DetailPair label="Total Recipients" value={totalRecipients} />
        <DetailPair label="Estimated Completion Time" value={estimatedTime} />
      </div>
      <div className="py-3 info lg:grid-cols-4">
        {sendSMS && (
          <Fragment>
            <input
              className="w-full form-input"
              placeholder="Test Phone Number"
              id="test-phone"
            />
            <div onClick={() => testHandler("sms")} className="btn-secondary">
              Send Test SMS
            </div>
          </Fragment>
        )}
        {sendEmail && (
          <Fragment>
            <input
              className="w-full form-input"
              placeholder="Test Email Address"
              id="test-email"
            />
            <div onClick={() => testHandler("email")} className="btn-secondary">
              Send Test Email
            </div>
          </Fragment>
        )}
      </div>

      <div className="info">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div onClick={confirmHandler} className="btn-primary">
            Confirm Announcement
          </div>
        )}
      </div>

      {result && (
        <div className="grid gap-6">
          <Heading title="Results" isBackArrow={false} />
          <div className="info">
            <DetailPair label="SMS Messages Sent" value={result?.sentSms} />
            <DetailPair label="Emails Sent" value={result?.sentEmails} />
            <DetailPair
              label="Announcement Record"
              classes="text-yellow-600 cursor-pointer underline"
              value={result?.id && "View Details"}
              onClick={(e) =>
                result?.id &&
                navigate(`/announcements/info?id=${result?.id}`, e)
              }
            />
          </div>
          <div className="grid grid-cols-2">
            <div className="list">
              <p className="text-stone-200">
                <span>Failed SMS:</span> {result?.failedSms?.length || 0}{" "}
                recipients
              </p>
              {result?.failedSms.map((record, idx) => (
                <div className="info lg:grid-cols-3" key={idx}>
                  {["Name", "Phone"].map((field) => (
                    <input
                      className="form-input-disabled"
                      value={record[field.toLowerCase()]}
                      disabled
                    />
                  ))}
                  <input
                    className="form-input-disabled"
                    value="SMS Failed"
                    disabled
                  />
                </div>
              ))}
            </div>
            <div className="list">
              <p className="text-stone-200">
                <span>Failed Emails:</span> {result?.failedEmails?.length || 0}{" "}
                recipients
              </p>
              {result?.failedEmails.map((record, idx) => (
                <div className="info lg:grid-cols-3" key={idx}>
                  {["Name", "Email"].map((field) => (
                    <input
                      className="form-input-disabled"
                      value={record[field.toLowerCase()]}
                      disabled
                    />
                  ))}
                  <input
                    className="form-input-disabled"
                    value="Email Failed"
                    disabled
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
